import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SearchFacet, SearchTabItem, BaselineFacetMap } from './../../../types/search';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { buildBaselineFacetMap } from '../../utils/SearchUtils';

export const useContentListing = (apiUrl: string, paginationDisplayLimit: number = 10, contentType: string = '') => {
  const dateFacetKeysRef = useRef<Set<string>>(new Set());

  // Core state
  const [searchParams, setSearchParams] = useState({
    page: 1,
    sortOrder: 'MostRecent',
    activeTab: contentType as string | null,
    filters: {} as Record<string, string[]>,
  });

  // UI state
  const [openFacets, setOpenFacets] = useState<Record<string, boolean>>({});
  const [previousResults, setPreviousResults] = useState<any[]>([]);
  const [previousTabs, setPreviousTabs] = useState<any[]>([]);
  const [previousFacets, setPreviousFacets] = useState<any[]>([]);
  const [urlParamsLoaded, setUrlParamsLoaded] = useState(false);

  const buildQueryString = useCallback(
    (params: typeof searchParams) => {
      const urlParams = new URLSearchParams();

      urlParams.set('page', params.page.toString());
      urlParams.set('order', params.sortOrder);
      urlParams.set('type', 'content');

      if (params.activeTab) {
        urlParams.set('pagetype', encodeURIComponent(params.activeTab));
      } else if (contentType) {
        urlParams.set('pagetype', encodeURIComponent(contentType));
      }

      Object.entries(params.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          urlParams.set(key, values.join(','));
        }
      });

      return `?${urlParams.toString()}`;
    },
    [contentType]
  );

  const updateUrl = useCallback(
    (params: typeof searchParams) => {
      const queryString = buildQueryString(params);
      globalThis.history.pushState({}, '', queryString);
    },
    [buildQueryString]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const initialParams = { ...searchParams };

    const urlPage = urlParams.get('page');
    if (urlPage) initialParams.page = Number.parseInt(urlPage, 10);

    const urlSort = urlParams.get('order');
    if (urlSort) initialParams.sortOrder = urlSort;

    const urlTab = urlParams.get('pagetype');
    if (urlTab) {
      initialParams.activeTab = urlTab;
    } else if (contentType) {
      initialParams.activeTab = contentType;
    }

    const urlFilters: Record<string, string[]> = {};
    urlParams.forEach((value, key) => {
      if (!['page', 'order', 'pagetype', 'type'].includes(key)) {
        urlFilters[key] = value.split(',');
      }
    });
    initialParams.filters = urlFilters;

    setSearchParams(initialParams);
    setUrlParamsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType]);

  const listingQuery = useQuery({
    queryKey: [
      'contentListing',
      {
        page: searchParams.page,
        sort: searchParams.sortOrder,
        tab: searchParams.activeTab ?? contentType ?? 'all',
        filters: JSON.stringify(searchParams.filters),
        contentType,
      },
    ],
    queryFn: async () => {
      const queryString = buildQueryString(searchParams);
      const apiParams = new URLSearchParams(queryString.substring(1));
      apiParams.set('pageSize', paginationDisplayLimit.toString());
      const apiQueryString = `?${apiParams.toString()}`;

      const url = `${apiUrl}${apiQueryString}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error('Content listing fetch failed');
      return await response.json();
    },
    staleTime: 60000,
    enabled: urlParamsLoaded && !!contentType,
    placeholderData: keepPreviousData,
  });

  const initialFacetItemsRef = useRef<BaselineFacetMap>({});

  const trackDateFacets = useCallback((facets: SearchFacet[]) => {
    facets.forEach(facet => {
      if (facet.type === 'date') {
        dateFacetKeysRef.current.add(facet.key);
      }
    });
  }, []);

  const initializeFacetBaseline = useCallback(
    (facets: SearchFacet[]) => {
      initialFacetItemsRef.current = buildBaselineFacetMap(facets);
      trackDateFacets(facets);
    },
    [trackDateFacets]
  );

  const shouldInitializeFacets = useCallback(() => {
    return (
      !listingQuery.isLoading && listingQuery.data?.facets && Object.keys(initialFacetItemsRef.current).length === 0
    );
  }, [listingQuery.isLoading, listingQuery.data?.facets]);

  const updatePreviousData = useCallback(() => {
    if (listingQuery.isLoading) return;

    if (listingQuery.data?.results) {
      setPreviousResults(listingQuery.data.results);
    }
    if (listingQuery.data?.tabs) {
      setPreviousTabs(listingQuery.data.tabs);
    }
    if (listingQuery.data?.facets) {
      setPreviousFacets(listingQuery.data.facets);
    }
  }, [listingQuery.isLoading, listingQuery.data?.results, listingQuery.data?.tabs, listingQuery.data?.facets]);

  useEffect(() => {
    if (shouldInitializeFacets()) {
      initializeFacetBaseline(listingQuery.data.facets as SearchFacet[]);
    }

    updatePreviousData();
  }, [
    listingQuery.isLoading,
    listingQuery.data?.facets,
    listingQuery.data?.results,
    listingQuery.data?.tabs,
    initializeFacetBaseline,
    shouldInitializeFacets,
    updatePreviousData,
  ]);

  const facets = useMemo(() => {
    const source = listingQuery.data?.facets ?? previousFacets;
    if (!source) return previousFacets;

    return source
      .filter((facet: SearchFacet) => facet.key !== 'pagetype' && facet.key !== 'tab')
      .map((facet: SearchFacet) => {
        const currentByValue = new Map((facet.items ?? []).map(i => [String(i.value), i]));

        const baseline =
          initialFacetItemsRef.current[facet.key] ??
          (facet.items ?? []).map(i => ({
            value: String(i.value),
            label: i.label,
            pagetype: (i as any).pagetype,
          }));

        const mergedItems = baseline.map(b => {
          const curr = currentByValue.get(b.value);
          const merged = {
            ...(curr ?? { value: b.value, label: b.label, total: 0, pagetype: b.pagetype }),
            isSelected: searchParams.filters[facet.key]?.includes(b.value) || false,
          };
          return merged;
        });

        return {
          ...facet,
          isOpen: openFacets[facet.key] || false,
          items: mergedItems,
          pagetype: facet.pagetype || searchParams.activeTab || contentType,
        };
      });
  }, [
    listingQuery.data?.facets,
    previousFacets,
    openFacets,
    searchParams.filters,
    searchParams.activeTab,
    contentType,
  ]);

  const facetsWithOpenState = useMemo(() => {
    return facets.map((facet: SearchFacet) => ({
      ...facet,
      isOpen: openFacets[facet.key] || false,
      items: (facet.items || []).map(item => ({
        ...item,
        isSelected: searchParams.filters[facet.key]?.includes(item.value) || false,
      })),
    }));
  }, [facets, openFacets, searchParams.filters]);

  const appliedFacetsWithState = useMemo(() => {
    return facets.map((facet: SearchFacet) => ({
      ...facet,
      items: (facet.items || []).map(item => ({
        ...item,
        isSelected: searchParams.filters[facet.key]?.includes(item.value) || false,
      })),
    }));
  }, [facets, searchParams.filters]);

  // Removed pendingFilters sync effect

  // Handlers
  const toggleFacet = useCallback((facetKey: string) => {
    setOpenFacets(prev => {
      const isCurrentlyOpen = prev[facetKey];

      if (isCurrentlyOpen) {
        return { ...prev, [facetKey]: false };
      }

      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });

      newState[facetKey] = true;
      return newState;
    });
  }, []);

  const toggleFilter = useCallback(
    (facetKey: string, itemValue: string) => {
      setSearchParams(prev => {
        const newFilters = JSON.parse(JSON.stringify(prev.filters));
        const values = [...(newFilters[facetKey] ?? [])];

        const index = values.indexOf(itemValue);
        if (index >= 0) {
          values.splice(index, 1);
        } else {
          values.push(itemValue);
        }

        if (values.length) {
          newFilters[facetKey] = values;
        } else {
          delete newFilters[facetKey];
        }

        const newParams = {
          ...prev,
          filters: newFilters,
          page: 1,
        };
        updateUrl(newParams);
        return newParams;
      });
    },
    [updateUrl]
  );

  // Remove applyFilters, not needed anymore

  const clearAllFilters = useCallback(() => {
    const emptyFilters = {};

    const newParams = {
      ...searchParams,
      filters: emptyFilters,
      page: 1,
    };

    setSearchParams(newParams);

    const urlParams = new URLSearchParams();
    urlParams.set('page', '1');
    urlParams.set('order', newParams.sortOrder);
    urlParams.set('type', 'content');

    if (newParams.activeTab) {
      urlParams.set('pagetype', encodeURIComponent(newParams.activeTab));
    } else if (contentType) {
      urlParams.set('pagetype', encodeURIComponent(contentType));
    }

    globalThis.history.pushState({}, '', `?${urlParams.toString()}`);

    listingQuery.refetch();
  }, [searchParams, listingQuery, contentType]);

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = { ...searchParams, page };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  const handleSortChange = useCallback(
    (newSortOrder: string) => {
      const newParams = { ...searchParams, sortOrder: newSortOrder };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  const removeFilter = useCallback(
    (facetKey: string, itemValue: string) => {
      const newFilters = JSON.parse(JSON.stringify(searchParams.filters));

      if (newFilters[facetKey]) {
        const values = newFilters[facetKey].filter((v: string) => v !== itemValue);
        if (values.length) {
          newFilters[facetKey] = values;
        } else {
          delete newFilters[facetKey];
        }
      }

      const newParams = {
        ...searchParams,
        filters: newFilters,
        page: 1,
      };

      setSearchParams(newParams);

      const urlParams = new URLSearchParams();
      urlParams.set('page', '1');
      urlParams.set('order', newParams.sortOrder);
      urlParams.set('type', 'content');

      if (newParams.activeTab) {
        urlParams.set('pagetype', encodeURIComponent(newParams.activeTab));
      } else if (contentType) {
        urlParams.set('pagetype', encodeURIComponent(contentType));
      }

      Object.entries(newFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          urlParams.set(key, values.join(','));
        }
      });

      globalThis.history.pushState({}, '', `?${urlParams.toString()}`);

      listingQuery.refetch();
    },
    [searchParams, listingQuery, contentType]
  );

  const sortOptions = useMemo(() => {
    const tabsFromApi: SearchTabItem[] =
      (listingQuery.data?.tabs as any[]) ?? (listingQuery.data?.summaryTabs as any[]) ?? [];

    if (tabsFromApi.length > 0) {
      const currentTab =
        tabsFromApi.find(t => t.key === (searchParams.activeTab ?? contentType ?? 'all')) ?? tabsFromApi[0];

      if (currentTab?.sortOptions?.length) {
        return currentTab.sortOptions;
      }
    }

    return listingQuery.data?.sortOptions ?? [];
  }, [
    listingQuery.data?.tabs,
    listingQuery.data?.summaryTabs,
    listingQuery.data?.sortOptions,
    searchParams.activeTab,
    contentType,
  ]);

  const tabs = useMemo(() => {
    if (!listingQuery.data?.tabs && previousTabs.length > 0) {
      return previousTabs.map((tab: SearchTabItem) => ({
        ...tab,
        isSelected: tab.key === searchParams.activeTab,
      }));
    }

    if (!listingQuery.data?.tabs) return [];

    return listingQuery.data.tabs.map((tab: SearchTabItem) => ({
      ...tab,
      isSelected: tab.key === searchParams.activeTab,
    }));
  }, [listingQuery.data?.tabs, searchParams.activeTab, previousTabs]);

  const filteredResults = useMemo(() => {
    if (listingQuery.isLoading && previousResults.length > 0) {
      return previousResults;
    }

    if (listingQuery.data?.results) {
      return listingQuery.data.results;
    }

    return [];
  }, [listingQuery.data?.results, listingQuery.isLoading, previousResults]);

  return {
    // Core state and derived state
    results: filteredResults,
    activeTabKey: searchParams.activeTab,
    sortOrder: searchParams.sortOrder,

    // React Query states
    isLoading: listingQuery.isLoading,
    isError: listingQuery.isError,
    isSuccess: listingQuery.isSuccess,
    isFetching: listingQuery.isFetching,
    isPlaceholderData: listingQuery.isPlaceholderData,
    summary: listingQuery.data?.summary,

    // UI state
    facets: facetsWithOpenState,
    appliedFilters: appliedFacetsWithState,
    tabs,
    sortOptions,
    pagination: {
      currentPage: searchParams.page,
      totalPages: listingQuery.data?.pagination?.totalPages ?? 1,
      totalResults: listingQuery.data?.pagination?.totalResults ?? 0,
      pageSize: paginationDisplayLimit,
    },

    // Actions
    toggleFacet,
    toggleFilter,
    removeFilter,
    clearAllFilters,
    handlePageChange,
    handleSortChange,
  };
};
